import { gql } from "@apollo/client";
import {
  AiRequestFragment,
  useGetAiRequestsQuery,
} from "@src/features/libraryItem/course/hooks/useCourseVersionAIRequests.generated";
import { usePollInterval } from "@src/lib/hooks/usePollInterval";
import { useCallback, useEffect, useMemo } from "react";

type Return = {
  aiRequests: AiRequestFragment[];
};

type Args = {
  courseVersionId?: number;
};

const useCourseVersionAIRequests = (args: Args): Return => {
  const noIncompleteAIRequestsPollInterval = usePollInterval(1000 * 30);
  const someIncompleteAIRequestsPollInterval = usePollInterval(1000);
  const {
    data: data,
    previousData: prevData,
    startPolling,
  } = useGetAiRequestsQuery({
    skip: !args.courseVersionId,
    variables: {
      input: { courseVersionId: args.courseVersionId },
    },
  });
  const aiRequests = useMemo(
    () => data?.GetAIRequests || prevData?.GetAIRequests || [],
    [data?.GetAIRequests, prevData?.GetAIRequests],
  );
  const slowDownAIRequestsPolling = useCallback(() => {
    startPolling(noIncompleteAIRequestsPollInterval);
  }, [noIncompleteAIRequestsPollInterval, startPolling]);
  const speedUpAIRequestsPolling = useCallback(() => {
    startPolling(someIncompleteAIRequestsPollInterval);
  }, [someIncompleteAIRequestsPollInterval, startPolling]);
  useEffect(() => {
    const incompleteAiRequests = aiRequests.filter(
      (aiRequest) =>
        !aiRequest.aiEditPromptRequest?.generationCompletedAt &&
        !aiRequest.aiEditPromptRequest?.generationErroredAt,
    );
    if (incompleteAiRequests.length === 0) {
      slowDownAIRequestsPolling();
    } else {
      speedUpAIRequestsPolling();
    }
  }, [
    aiRequests,
    noIncompleteAIRequestsPollInterval,
    someIncompleteAIRequestsPollInterval,
    slowDownAIRequestsPolling,
    speedUpAIRequestsPolling,
    startPolling,
  ]);
  return useMemo(() => {
    return {
      aiRequests,
    };
  }, [aiRequests]);
};

gql`
  mutation EditPromptWithAI($input: EditPromptWithAIInput!) {
    editPromptWithAI(input: $input) {
      success
      error {
        code
      }
      aiRequest {
        ...AIRequestThin
      }
    }
  }

  fragment AIRequest on AIRequest {
    ...AIRequestThin
    requestText
    aiEditPromptRequest {
      ...AIEditPromptRequest
    }
  }

  fragment AIResponseOption on AIResponseOption {
    id
    answer {
      ...TranslationSet
    }
    index
    isCorrect
  }

  fragment AIEditPromptRequest on AIEditPromptRequest {
    id
    prompt {
      id
      uuid
    }
    response
    requestType
    aiResponseOptions {
      ...AIResponseOption
    }
    generationStartedAt
    generationCompletedAt
    generationErroredAt
  }

  query GetAIRequests($input: GetAIRequestsInput!) {
    GetAIRequests(input: $input) {
      ...AIRequest
    }
  }

  mutation ResolveAIRequest($input: ResolveAIRequestInput!) {
    resolveAIRequest(input: $input) {
      success
      error {
        code
      }
      aiRequest {
        ...AIRequestThin
      }
    }
  }
`;

export default useCourseVersionAIRequests;
